import type { Dayjs } from 'dayjs';
import * as Yup from 'yup';

import { EPosSystem, EStationVariant, EFuelType, EFuelGrade } from '@/config/station';
import { WEEKDAYS } from '@/config/time';
import { isEmpty } from '@/utils/commonUtils';

import type { TWeekday } from '@/types/common';

import {
  locationSchema,
  makeEnumString,
  requiredTimeRange,
  requiredEmail,
  requiredPhone,
  requiredString,
  requiredNumber,
} from '@/validation/common';

export const stationGeneralInfoSchema = Yup.object({
  brand: requiredString,
  name: requiredString.min(2, { key: 'errors.form.minLabel', min: 2 }),
  phone: Yup.array().of(requiredPhone).default([requiredPhone.getDefault()]),
  email: Yup.array().of(requiredEmail).default([requiredEmail.getDefault()]),
  location: locationSchema.required('errors.form.required'),
}).required();

export const stationUpdateInfoSchema = stationGeneralInfoSchema.omit(['location']);

const stationPosConnectionSchema = Yup.object({
  service: makeEnumString(EStationVariant).default(EStationVariant.GASOLINE_AND_DIESEL),
  system: makeEnumString(EPosSystem).default(EPosSystem.VERIFONE),
}).required();

export const stationPosInfoSchema = Yup.array().of(stationPosConnectionSchema).ensure();

const discountSchema = Yup.number().positive('errors.form.positive');

export const stationAddPumpSchema = Yup.object({
  fuelType: makeEnumString(EFuelType).required(),
  fuelGrade: makeEnumString(EFuelGrade).required(),
  octaneLevel: requiredNumber
    .min(1, { key: 'errors.form.minNumber', min: 1 })
    .max(100, { key: 'errors.form.maxNumber', max: 100 }),
  price: requiredNumber.positive('errors.form.positive'),
  minDiscount: discountSchema,
  maxDiscount: discountSchema.when('minDiscount', (minDiscount, schema) =>
    minDiscount
      ? schema.min(minDiscount, ({ min }: { min: string }) => ({
          key: 'errors.form.moreOrEqual',
          value: min,
        }))
      : schema,
  ),
});

export const stationEditPumpSchema = Yup.object({
  fuelType: makeEnumString(EFuelType).required(),
  fuelGrade: makeEnumString(EFuelGrade).required(),
  price: requiredNumber.positive('errors.form.positive'),
  minDiscount: discountSchema,
  maxDiscount: discountSchema.when('minDiscount', (minDiscount, schema) =>
    minDiscount
      ? schema.min(minDiscount, ({ min }: { min: string }) => ({
          key: 'errors.form.moreOrEqual',
          value: min,
        }))
      : schema,
  ),
});

const breaks = Yup.array()
  .of(requiredTimeRange)
  .onlyIf('isOpen')
  .test('breakWithinOpHours', (value, { parent, createError }) => {
    const { startTime, endTime } = parent.workingHours;

    const validate = (date: Dayjs | null) =>
      date && ((startTime && date < startTime) || (endTime && date > endTime))
        ? 'station.operationHours.breakWithinHours'
        : undefined;

    const errors = value?.map((range) => ({
      startTime: validate(range?.startTime),
      endTime: validate(range?.endTime),
    }));

    if (!errors || errors.every(isEmpty)) {
      return true;
    }

    return createError({ message: { ...errors } });
  });

const stationDayOpHoursSchema = Yup.object({
  isOpen: Yup.boolean().required(),
  workingHours: requiredTimeRange.onlyIf('isOpen'),
  breaks,
}).required();

export const stationOpHoursSchema = Yup.object(
  WEEKDAYS.reduce(
    (acc, day) => ({ ...acc, [day]: stationDayOpHoursSchema }),
    {} as Record<TWeekday, typeof stationDayOpHoursSchema>,
  ),
).required();
function minimumString(arg0: number, arg1: { key: string; min: number }): any {
  throw new Error('Function not implemented.');
}
